import React from 'react';

const SentimentChart = ({ data, onChartClick }) => {
  if (!data || !data.distribution) {
    return (
      <div className="text-center py-8">
        <i className="bi bi-graph-up-arrow text-4xl text-blue-500 mb-3 block"></i>
        <p className="text-slate-600">Aucune donnée de sentiment disponible</p>
      </div>
    );
  }

  const { distribution, overall_sentiment } = data;
  
  // Couleurs pour les différentes catégories de sentiment
  const colors = {
    positive: '#10b981',
    neutral: '#6b7280', 
    negative: '#ef4444'
  };

  return (
    <div className="space-y-6" onClick={() => onChartClick && onChartClick('sentiment')}>
      {/* Graphique en camembert pour la distribution */}
      <div>
        <h6 className="font-semibold mb-4 text-slate-700">Distribution des sentiments</h6>
        <div className="flex items-center justify-center">
          <div className="relative w-48 h-48">
            <svg width="100%" height="100%" viewBox="0 0 42 42" className="donut">
              {distribution.map((item, index) => {
                const total = distribution.reduce((sum, d) => sum + d.percentage, 0);
                const percentage = total > 0 ? (item.percentage / total) * 100 : 0;
                const circumference = 2 * Math.PI * 15.9155;
                const strokeDasharray = `${(percentage * circumference) / 100} ${circumference}`;
                
                return (
                  <circle
                    key={item.category}
                    cx="21"
                    cy="21"
                    r="15.9155"
                    fill="transparent"
                    stroke={colors[item.category]}
                    strokeWidth="3"
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={index === 0 ? 0 : -distribution.slice(0, index).reduce((sum, d) => sum + (d.percentage * circumference) / 100, 0)}
                    transform="rotate(-90 21 21)"
                  />
                );
              })}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-800">
                  {overall_sentiment?.average ? (overall_sentiment.average * 100).toFixed(1) : 0}%
                </div>
                <div className="text-xs text-slate-500">Moyenne</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Légende */}
        <div className="flex justify-center space-x-6 mt-4">
          {distribution.map(item => (
            <div key={item.category} className="flex items-center">
              <div 
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: colors[item.category] }}
              ></div>
              <span className="text-sm text-slate-600 capitalize">
                {item.category}: {item.percentage.toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline des sentiments si disponible */}
      {data.timeline && data.timeline.length > 0 && (
        <div>
          <h6 className="font-semibold mb-4 text-slate-700">Évolution temporelle</h6>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="h-32 relative">
              {/* Graphique linéaire simplifié */}
              <svg width="100%" height="100%" className="min-h-[128px]">
                {data.timeline.map((point, index) => {
                  const x = (index / (data.timeline.length - 1)) * 100;
                  const y = 100 - (point.sentiment * 100);
                  
                  return (
                    <g key={index}>
                      <circle
                        cx={`${x}%`}
                        cy={`${y}%`}
                        r="3"
                        fill="#3b82f6"
                      />
                      {index > 0 && (
                        <line
                          x1={`${(index - 1) / (data.timeline.length - 1) * 100}%`}
                          y1={`${100 - (data.timeline[index - 1].sentiment * 100)}%`}
                          x2={`${x}%`}
                          y2={`${y}%`}
                          stroke="#3b82f6"
                          strokeWidth="2"
                        />
                      )}
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Métriques résumées */}
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <div className="text-2xl font-bold text-green-600">
            {distribution.find(d => d.category === 'positive')?.count || 0}
          </div>
          <div className="text-xs text-slate-500">Positifs</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-600">
            {distribution.find(d => d.category === 'neutral')?.count || 0}
          </div>
          <div className="text-xs text-slate-500">Neutres</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-red-600">
            {distribution.find(d => d.category === 'negative')?.count || 0}
          </div>
          <div className="text-xs text-slate-500">Négatifs</div>
        </div>
      </div>
    </div>
  );
};

export default SentimentChart;